import {
  suggestPrescriptionCapsules,
  type PrescriptionFrequencyShortcut,
} from '@/shared/prescription/suggestPrescriptionCapsules'

export const DOCUMENT_DRAFT_PARSE_ERROR_MESSAGE =
  'Não foi possível ler a sugestão. Tente de novo.'

const FREQUENCY_SHORTCUTS: ReadonlySet<string> = new Set([
  '4h',
  '6h',
  '8h',
  '12h',
  '1d',
  'dor',
])

export type DocumentDraftResponseType = 'prescription' | 'certificate' | 'request' | 'generic'

export interface ParsedTextDocumentDraft {
  kind: 'text'
  contentDraft: string
}

export interface ParsedPrescriptionItemDraft {
  medicationName: string
  frequencyShortcut: PrescriptionFrequencyShortcut | null
  scheduleText: string
  periodAmount: number | null
  periodUnit: 'dias' | 'meses' | null
  capsules: number
  inclusionLine: string
  instructions: string
}

export interface ParsedPrescriptionDocumentDraft {
  kind: 'prescription'
  items: ParsedPrescriptionItemDraft[]
}

export type ParsedDocumentDraft = ParsedTextDocumentDraft | ParsedPrescriptionDocumentDraft

function invalidSuggestion(): never {
  throw new Error(DOCUMENT_DRAFT_PARSE_ERROR_MESSAGE)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readOptionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function readMedicationName(item: Record<string, unknown>): string {
  const medicationName = readOptionalString(item.medicationName)?.trim() ?? ''
  const name = readOptionalString(item.name)?.trim() ?? ''
  const chosen = medicationName || name
  if (!chosen) invalidSuggestion()
  return chosen
}

function readScheduleText(item: Record<string, unknown>): string {
  const scheduleText = readOptionalString(item.scheduleText)?.trim() ?? ''
  if (scheduleText) return scheduleText
  return readOptionalString(item.phrase)?.trim() ?? ''
}

function readFrequencyShortcut(value: unknown): PrescriptionFrequencyShortcut | null {
  if (typeof value !== 'string') return null
  const shortcut = value.trim()
  if (!FREQUENCY_SHORTCUTS.has(shortcut)) return null
  return shortcut as PrescriptionFrequencyShortcut
}

function readPeriodAmount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const normalized = value.trim().replace(',', '.')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function readPeriodUnit(value: unknown): 'dias' | 'meses' | null {
  if (value === 'dias' || value === 'meses') return value
  return null
}

function parseTextDraft(payload: unknown): ParsedTextDocumentDraft {
  if (!isRecord(payload)) invalidSuggestion()
  const contentDraft = readOptionalString(payload.contentDraft)?.trim() ?? ''
  if (!contentDraft) invalidSuggestion()
  return { kind: 'text', contentDraft }
}

function parsePrescriptionItem(raw: unknown): ParsedPrescriptionItemDraft {
  if (!isRecord(raw)) invalidSuggestion()

  const medicationName = readMedicationName(raw)
  const scheduleText = readScheduleText(raw)
  const frequencyShortcut = readFrequencyShortcut(raw.frequencyShortcut)
  const periodAmount = readPeriodAmount(raw.periodAmount)
  const periodUnit = readPeriodUnit(raw.periodUnit)
  const suggestion = suggestPrescriptionCapsules({
    name: medicationName,
    phrase: scheduleText,
    shortcut: frequencyShortcut,
    amount: periodAmount,
    unit: periodUnit,
  })

  return {
    medicationName,
    frequencyShortcut,
    scheduleText,
    periodAmount,
    periodUnit,
    capsules: suggestion.capsules,
    inclusionLine: suggestion.line ?? medicationName,
    instructions: readOptionalString(raw.instructions)?.trim() ?? '',
  }
}

function parsePrescriptionDraft(payload: unknown): ParsedPrescriptionDocumentDraft {
  if (!isRecord(payload) || !Array.isArray(payload.items)) invalidSuggestion()

  const items: ParsedPrescriptionItemDraft[] = []
  for (const raw of payload.items) {
    items.push(parsePrescriptionItem(raw))
  }
  return { kind: 'prescription', items }
}

/** Lê o JSON da sugestão. Receita vira itens com cápsulas recalculadas; os outros tipos continuam texto. */
export function parseDocumentDraftPayload(
  type: DocumentDraftResponseType,
  payload: unknown,
): ParsedDocumentDraft {
  if (type === 'prescription') return parsePrescriptionDraft(payload)
  return parseTextDraft(payload)
}
